<template>
  <VueTelInput v-bind="bindings" v-model="model" class="c-tel-input">
    <template #arrow-icon>
      <CDirectionIcon :direction="isOpen? 'up' : 'down'" class="u-ml-1"/>
    </template>
  </VueTelInput>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { VueTelInput } from 'vue-tel-input';
  import 'vue-tel-input/vue-tel-input.css';
  import { CountryCode, getCountryCode, getDefaultCountryId, KNOWN_COUNTRY_CODES } from '../../models/Country';
  import { COUNTRY_PREFIX_EXP, extendedMobileValidation } from '../../utils/form/validation-legacy';
  import CDirectionIcon from '../CDirectionIcon.vue';

  const props = defineProps<{
    countryCode?: CountryCode;
  }>();

  const emit = defineEmits<{
    (e: 'update:warning', v: string)
  }>();

  const i18n = useI18n();
  const model = defineModel<string | null>();
  const valid = ref<boolean | undefined>(undefined);
  const isOpen = ref(false);

  const bindings = computed(() => {
    return {
      validCharactersOnly: true,
      defaultCountry: props.countryCode ?? getCountryCode(getDefaultCountryId()),
      autoDefaultCountry: false,
      preferredCountries: KNOWN_COUNTRY_CODES,
      dropdownOptions: {
        showDialCodeInSelection: false,
        showDialCodeInList: false,
        showFlags: true,
      },
      inputOptions: {
        showDialCode: true,
      },
      onOpen: () => isOpen.value = true,
      onClose: () => isOpen.value = false,
      onValidate: result => valid.value = result.valid,
      onInput,
    };
  });

  watch(() => valid.value === false || !extendedMobileValidation(model.value), hasWarning => {
    emit('update:warning', hasWarning ? i18n.t('validation.phoneNumber') : '');
  });

  function onInput(value) {
    if (value === model.value || COUNTRY_PREFIX_EXP.test(value)) return; // ignore default values which contains only the country prefix
    model.value = value.target._value;
  }
</script>

<style lang="scss">
  .c-tel-input {
    &.vue-tel-input {
      border-radius: 0;
      border: none;
    }

    .vti__dropdown {
      position: static;
      margin-right: 0;
      padding: 0;

      &-list {
        border-radius: $input-radius;
        width: auto;
        max-width: 100%;
        top: 100%;
        margin-top: .25rem;
        margin-left: 1px;
      }
    }

    .vti__input {
      font-family: inherit;
      font-size: 1rem;
      font-weight: inherit;
      line-height: 1.125;
      height: 18px;
    }
  }
</style>